<template>
  <div class="settings-page">
    <el-card shadow="never">
      <div slot="header" class="section-header"><strong>系统配置</strong><span>管理全局业务参数，内置参数的键名不可修改</span></div>
      <el-form :inline="true" size="small" class="query-form" @submit.native.prevent="search">
        <el-form-item label="配置名称"><el-input v-model="query.name" clearable placeholder="请输入配置名称" maxlength="100" @keyup.enter.native="search" /></el-form-item>
        <el-form-item label="参数键名"><el-input v-model="query.code" clearable placeholder="请输入参数键名" maxlength="100" @keyup.enter.native="search" /></el-form-item>
        <el-form-item><el-button type="primary" icon="el-icon-search" @click="search">查询</el-button><el-button icon="el-icon-refresh-left" @click="reset">重置</el-button></el-form-item>
      </el-form>
      <div class="table-actions"><el-button v-if="hasPermission('system:config:add')" type="primary" size="small" icon="el-icon-plus" @click="openForm()">新增配置</el-button><el-button v-if="hasPermission('system:config:export')" size="small" icon="el-icon-download" :loading="exporting" @click="exportData">导出</el-button></div>
      <el-alert v-if="errorMessage" :title="errorMessage" type="error" :closable="false" show-icon />
      <el-table v-loading="loading" :data="list" border stripe row-key="id">
        <el-table-column type="index" label="序号" width="65" align="center" :index="indexMethod" />
        <el-table-column prop="name" label="配置名称" min-width="160" show-overflow-tooltip />
        <el-table-column prop="configKey" label="参数键名" min-width="190" show-overflow-tooltip />
        <el-table-column label="参数键值" min-width="210" show-overflow-tooltip><template slot-scope="{ row }">{{ settingValue(row) }}</template></el-table-column>
        <el-table-column label="内置" width="75" align="center"><template slot-scope="{ row }"><el-tag :type="row.builtIn ? '' : 'info'" size="small">{{ row.builtIn ? '是' : '否' }}</el-tag></template></el-table-column>
        <el-table-column prop="remark" label="备注" min-width="180" show-overflow-tooltip />
        <el-table-column label="创建时间" width="170"><template slot-scope="{ row }">{{ formatTime(row.createTime) }}</template></el-table-column>
        <el-table-column label="操作" width="140" fixed="right"><template slot-scope="{ row }"><el-button v-if="hasPermission('system:config:edit')" type="text" size="small" @click="openForm(row)">编辑</el-button><el-button v-if="hasPermission('system:config:delete')" type="text" size="small" :disabled="row.builtIn" class="danger-text" @click="remove(row)">删除</el-button></template></el-table-column>
      </el-table>
      <el-pagination class="pagination" background :current-page.sync="query.pageNum" :page-size.sync="query.pageSize" :page-sizes="[10, 20, 50, 100]" :total="total" layout="total, sizes, prev, pager, next, jumper" @size-change="search" @current-change="fetchList" />
    </el-card>
    <el-dialog :title="form.id ? '编辑系统配置' : '新增系统配置'" :visible.sync="dialogVisible" width="580px" :close-on-click-modal="false">
      <el-form ref="settingForm" :model="form" :rules="rules" label-width="95px">
        <el-form-item label="配置名称" prop="name"><el-input v-model="form.name" maxlength="100" show-word-limit /></el-form-item>
        <el-form-item label="参数键名" prop="configKey"><el-input v-model="form.configKey" :disabled="form.builtIn" maxlength="100" placeholder="例如：business.contact" /></el-form-item>
        <el-form-item label="参数键值"><el-input v-if="formSensitive" v-model="form.configValue" type="password" show-password autocomplete="new-password" maxlength="2000" :placeholder="form.id ? '留空保持不变' : '请输入参数键值'" /><el-input v-else v-model="form.configValue" type="textarea" :rows="4" maxlength="2000" show-word-limit /></el-form-item>
        <el-form-item label="备注"><el-input v-model="form.remark" type="textarea" :rows="3" maxlength="500" show-word-limit /></el-form-item>
      </el-form>
      <span slot="footer"><el-button :disabled="saving" @click="dialogVisible = false">取消</el-button><el-button type="primary" :loading="saving" v-permission="form.id ? 'system:config:edit' : 'system:config:add'" @click="save">保存</el-button></span>
    </el-dialog>
  </div>
</template>

<script>
import request from '@/utils/request'
import { hasPermission } from '@/utils/auth'
import { saveAs } from 'file-saver'

const emptyQuery = () => ({ name: '', code: '', pageNum: 1, pageSize: 20 })
const emptyForm = () => ({ id: '', name: '', configKey: '', configValue: '', remark: '', builtIn: false, sensitive: false, valueConfigured: false })
const sensitiveKeys = ['in-transit.track.password', 'in-transit.track.app-secret']

export default {
  name: 'SystemSettings',
  data() {
    return {
      list: [], total: 0, query: emptyQuery(), form: emptyForm(),
      loading: false, saving: false, exporting: false, dialogVisible: false, errorMessage: '', requestId: 0,
      rules: { name: [{ required: true, whitespace: true, message: '请输入配置名称', trigger: 'blur' }], configKey: [{ required: true, whitespace: true, message: '请输入参数键名', trigger: 'blur' }] }
    }
  },
  created() { this.fetchList() },
  beforeDestroy() { this.requestId++ },
  computed: {
    formSensitive() { return this.isSensitiveSetting(this.form) }
  },
  methods: {
    hasPermission,
    isSensitiveSetting(row) { return row.sensitive || sensitiveKeys.includes((row.configKey || '').trim()) },
    settingValue(row) { return this.isSensitiveSetting(row) ? (row.valueConfigured ? '已配置' : '未配置') : row.configValue },
    indexMethod(index) { return (this.query.pageNum - 1) * this.query.pageSize + index + 1 },
    formatTime(value) { return value ? String(value).replace('T', ' ').slice(0, 19) : '-' },
    search() { this.query.pageNum = 1; this.fetchList() },
    reset() { this.query = emptyQuery(); this.fetchList() },
    async fetchList() {
      const requestId = ++this.requestId
      this.loading = true
      this.errorMessage = ''
      try {
        const response = await request.get('/webserver/system/settings', { params: this.query })
        if (requestId !== this.requestId) return
        this.list = response.data.items
        this.total = response.data.total
        if (!this.list.length && this.query.pageNum > 1) { this.query.pageNum--; return this.fetchList() }
      } catch (error) { if (requestId === this.requestId) this.errorMessage = error.msg || '读取配置失败，请重试' }
      finally { if (requestId === this.requestId) this.loading = false }
    },
    openForm(row) {
      this.form = row ? { ...row, configValue: this.isSensitiveSetting(row) ? '' : row.configValue } : emptyForm()
      this.dialogVisible = true
      this.$nextTick(() => this.$refs.settingForm && this.$refs.settingForm.clearValidate())
    },
    save() {
      this.$refs.settingForm.validate(async valid => {
        if (!valid || this.saving) return
        this.saving = true
        const { id, name, configKey, configValue, remark } = this.form
        try {
          if (id) await request.put(`/webserver/system/settings/${id}`, { name, configKey, configValue, remark })
          else await request.post('/webserver/system/settings', { name, configKey, configValue, remark })
          this.$message.success('配置已保存')
          this.dialogVisible = false
          await this.fetchList()
        } catch (error) { this.$message.error(error.msg || '保存失败') }
        finally { this.saving = false }
      })
    },
    async remove(row) {
      try {
        await this.$confirm(`确认删除配置“${row.name}”吗？`, '删除配置', { type: 'warning' })
        await request.delete(`/webserver/system/settings/${row.id}`)
        this.$message.success('配置已删除')
        await this.fetchList()
      } catch (error) { if (error !== 'cancel' && error !== 'close') this.$message.error(error.msg || '删除失败') }
    },
    async exportData() {
      this.exporting = true
      try {
        const response = await request.get('/webserver/system/settings/export', { params: this.query, responseType: 'blob' })
        if (response.data.type.includes('json')) {
          const body = JSON.parse(await response.data.text())
          throw { msg: body.msg || '导出失败' }
        }
        saveAs(response.data, '系统配置.xlsx')
      } catch (error) { this.$message.error(error.msg || '导出失败') }
      finally { this.exporting = false }
    }
  }
}
</script>

<style scoped>
.settings-page { padding: 18px; min-height: 100%; background: #f4f6f9; box-sizing: border-box; }
.section-header { display: flex; align-items: center; gap: 14px; }
.section-header strong { font-size: 16px; }
.section-header span { color: #909399; font-size: 13px; }
.query-form .el-input { width: 220px; }
.table-actions { margin-bottom: 14px; }
.pagination { margin-top: 18px; text-align: right; }
.danger-text:not(.is-disabled) { color: #f56c6c; }
.el-alert { margin-bottom: 12px; }
</style>
